/**
 * Browser-native Deepgram Voice Agent client (no Node `ws` dependency).
 * Auth: Sec-WebSocket-Protocol bearer,<jwt>
 *
 * Includes inactivity detection:
 * - warn after warnMs of silence (default 45s)
 * - if still silent for endMs more (default 20s), speak goodbye and disconnect
 * - end_call function (handled by host UI) can schedule disconnect after speech
 */
export class DeviitorVoiceClient {
  constructor({ tokenFactory, agent, audio, onEvent, inactivity } = {}) {
    this.tokenFactory = tokenFactory;
    this.agent = agent;
    this.audio = audio || {
      input: { encoding: 'linear16', sample_rate: 16000 },
      output: { encoding: 'linear16', sample_rate: 24000, container: 'none' },
    };
    this.onEvent = onEvent || (() => {});
    this.ws = null;
    this.settingsApplied = false;
    this.intentionalClose = false;
    this.keepAliveTimer = null;
    this.audioContext = null;
    this.micStream = null;
    this.processor = null;
    this.playbackContext = null;
    this.nextPlayTime = 0;
    this.outputSampleRate = this.audio.output?.sample_rate || 24000;
    this.inputSampleRate = this.audio.input?.sample_rate || 16000;

    const ia = inactivity || {};
    this.inactivityEnabled = ia.enabled !== false;
    this.inactivityWarnMs = Number(ia.warnMs) > 0 ? Number(ia.warnMs) : 90_000;
    this.inactivityEndMs = Number(ia.endMs) > 0 ? Number(ia.endMs) : 45_000;
    this.inactivityWarnMessage =
      ia.warnMessage ||
      'Are you still there? Just say something if you would like to continue.';
    this.inactivityEndMessage =
      ia.endMessage ||
      'I have not heard back, so I will end this call now. Goodbye, and feel free to start again anytime.';
    this._inactivityPhase = 'idle'; // idle | warned | ending
    this._inactivityTimer = null;
    this._pendingEndAfterSpeech = null;
    this._endFallbackTimer = null;
    this._awaitingFarewellClose = false;
    this._farewellForceTimer = null;
    this._farewellCloseTimer = null;
    this.micMuted = false;
    /** True while Deepgram marks the agent as speaking (TTS streaming). */
    this._agentSpeaking = false;
    /** Soft mute uplink so speaker echo is not sent back to Deepgram. */
    this._suppressMicUplink = false;
    this._unmuteUplinkTimer = null;
    this._activeSources = new Set();
    /** Open tool calls — keep mic muted so echo cannot cancel Deepgram mid-tool. */
    this._pendingFunctionCalls = 0;
    this._functionProgressTimers = new Set();
  }

  emit(type, payload) {
    // Do not spread payload.type over the event name — Deepgram messages also have a `type` field
    // (e.g. FunctionCallRequest), which would overwrite the event and break handlers.
    this.onEvent({ ...(payload || {}), type });
  }

  static looksLikeUserFarewell(text) {
    // Keep this STRICT for accidental mid-call hangs, but allow common lead-ins:
    // "Okay. Bye.", "Thanks, bye", "Alright goodbye".
    const t = String(text || '').trim();
    if (!t || /\?/.test(t)) return false;

    const closing =
      "(?:good\\s*bye|goodbye|bye-?bye|bye(?:\\s+for\\s+now)?|see you(?:\\s+later)?|talk soon|hang up|end (?:the )?call|i(?:'?m| am) (?:gonna |going to )?(?:hang up|leave|go)|gotta go)";
    const leadIn =
      '(?:ok(?:ay)?|alright|all right|thanks?(?:\\s+you)?|thank you|cool|great|perfect|got it|yes|yep|yeah|sure)';

    if (new RegExp(`^(?:${leadIn}[.!,]*\\s+)+${closing}[.!]*\\s*$`, 'i').test(t)) return true;
    if (new RegExp(`^${closing}[.!]*\\s*$`, 'i').test(t)) return true;
    // Short utterance ending in bye (e.g. "Okay bye", "Ok bye now")
    if (t.length <= 48 && new RegExp(`\\b${closing}\\b[.!]*\\s*$`, 'i').test(t)) return true;
    return false;
  }

  /** Short agent goodbye after the user already said bye (end_call may have been skipped). */
  static looksLikeClosingGoodbye(text) {
    const t = String(text || '').trim();
    if (!t || t.length > 80 || /\?/.test(t)) return false;
    return /^(?:(?:ok(?:ay)?|alright|sure|thanks?)[.!,]*\s+)*(?:good\s*bye|goodbye|bye-?bye|bye)[.!]*\s*$/i.test(
      t,
    );
  }

  static looksLikeFarewell(text) {
    return DeviitorVoiceClient.looksLikeUserFarewell(text);
  }

  async connect() {
    this.intentionalClose = false;
    const token = await this.tokenFactory();
    await new Promise((resolve, reject) => {
      const ws = new WebSocket('wss://agent.deepgram.com/v1/agent/converse', [
        'bearer',
        token,
      ]);
      ws.binaryType = 'arraybuffer';
      this.ws = ws;

      const onFail = (err) => {
        cleanup();
        reject(err instanceof Error ? err : new Error(String(err)));
      };

      const cleanup = () => {
        ws.removeEventListener('open', onOpen);
        ws.removeEventListener('error', onFail);
      };

      const onOpen = () => {
        cleanup();
        this.emit('connected');
        resolve();
      };

      ws.addEventListener('open', onOpen);
      ws.addEventListener('error', onFail);
      ws.addEventListener('close', (ev) => {
        this._stopKeepAlive();
        this._stopInactivityWatch();
        this.settingsApplied = false;
        this.emit('disconnected', { reason: `${ev.code} ${ev.reason || ''}`.trim() });
        if (!this.intentionalClose) {
          this.emit('error', { message: `Socket closed: ${ev.code} ${ev.reason || ''}` });
        }
      });
      ws.addEventListener('message', (ev) => this._onMessage(ev.data));
    });
  }

  _onMessage(data) {
    if (data instanceof ArrayBuffer) {
      this._playPcm(data);
      return;
    }
    let msg;
    try {
      msg = JSON.parse(typeof data === 'string' ? data : new TextDecoder().decode(data));
    } catch {
      return;
    }

    if (msg.type === 'Welcome') {
      this._sendSettings();
      this.emit('welcome', msg);
      return;
    }

    if (msg.type === 'SettingsApplied') {
      this.settingsApplied = true;
      this._startKeepAlive();
      this._startInactivityWatch();
      this.emit('settings-applied', msg);
      return;
    }

    if (msg.type === 'ConversationText') {
      const isUser = String(msg.role || '').toLowerCase() === 'user';
      this._noteActivity('conversation', { isUser });
      this.emit('conversation-text', msg);
      if (isUser) {
        this._handleUserFarewellText(msg.content);
      } else if (this._awaitingFarewellClose) {
        // Agent kept asking questions — cancel auto-hangup.
        const continued =
          /\?/.test(String(msg.content || '')) || String(msg.content || '').length > 80;
        if (continued) {
          this._clearFarewellTimers();
          this._awaitingFarewellClose = false;
          this.emit('farewell-cleared', { reason: 'agent_continued' });
        } else if (DeviitorVoiceClient.looksLikeClosingGoodbye(msg.content)) {
          // User already said bye; agent said goodbye but may have skipped end_call.
          this.emit('farewell-detected', { role: 'agent', content: msg.content });
          this.endCallAfterConfirmation({
            reason: 'agent_goodbye_after_user',
            fallbackMs: 2000,
          });
        }
      }
      return;
    }

    if (msg.type === 'UserStartedSpeaking') {
      this._noteActivity('user-speaking', { isUser: true });
      // User speaking again cancels a pending farewell auto-hangup (they continued).
      if (this._awaitingFarewellClose && this._inactivityPhase !== 'ending') {
        this._clearFarewellTimers();
        this._awaitingFarewellClose = false;
        this.emit('farewell-cleared', { reason: 'user_spoke' });
      }
      // Ignore echo barge-in while agent audio is still playing / uplink is suppressed.
      if (!this._shouldAllowBargeIn()) {
        this.emit('barge-in-ignored', { reason: 'agent_playback' });
        return;
      }
      this._interruptPlayback();
      this.emit('user-started-speaking', msg);
      return;
    }

    if (msg.type === 'AgentStartedSpeaking') {
      this._noteActivity('agent-speaking', { isUser: false });
      this._agentSpeaking = true;
      this._setMicUplinkSuppressed(true, { hold: true });
      this.emit('agent-started-speaking', msg);
      return;
    }

    if (msg.type === 'AgentAudioDone') {
      this._agentSpeaking = false;
      // If a tool call is in flight, stay muted — echo during the wait cancels the agent turn.
      if (this._pendingFunctionCalls > 0) {
        this._setMicUplinkSuppressed(true, { hold: true });
      } else {
        this._scheduleUnmuteAfterPlayback(450);
      }
      this.emit('agent-audio-done', msg);
      this._maybeFinishPendingEnd('agent-audio-done');
      return;
    }

    if (msg.type === 'FunctionCallRequest') {
      this._noteActivity('function-call', { isUser: false });
      const count = Array.isArray(msg.functions) ? msg.functions.length : 1;
      this.beginFunctionCalls(count);
      this.emit('function-call-request', msg);
      return;
    }

    if (msg.type === 'AgentThinking') {
      this._noteActivity('thinking', { isUser: false });
      // Thinking/tool prep — keep echo out so the turn is not cancelled.
      this._setMicUplinkSuppressed(true, { hold: true });
      this.emit('agent-thinking', msg);
      return;
    }

    if (msg.type === 'InjectionRefused') {
      this.emit('injection-refused', msg);
      return;
    }

    if (msg.type === 'Error') {
      this.emit('error', msg);
      return;
    }

    if (msg.type === 'Warning') {
      this.emit('warning', msg);
      return;
    }

    this.emit('message', { ...msg, messageType: msg.type });
  }

  _sendSettings() {
    const settings = {
      type: 'Settings',
      audio: {
        input: {
          encoding: this.audio.input?.encoding || 'linear16',
          sample_rate: this.inputSampleRate,
        },
        output: {
          encoding: this.audio.output?.encoding || 'linear16',
          sample_rate: this.outputSampleRate,
          container: this.audio.output?.container || 'none',
        },
      },
      agent: this.agent,
    };
    this.ws.send(JSON.stringify(settings));
  }

  _startKeepAlive() {
    this._stopKeepAlive();
    // Deepgram CLIENT_MESSAGE_TIMEOUT fires if neither audio nor KeepAlive arrives soon enough.
    this.keepAliveTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'KeepAlive' }));
      }
    }, 3000);
  }

  _stopKeepAlive() {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  injectAgentMessage(message, behavior = 'queue') {
    if (this.ws?.readyState !== WebSocket.OPEN || !message) return false;
    this.ws.send(
      JSON.stringify({
        type: 'InjectAgentMessage',
        message,
        behavior,
      }),
    );
    return true;
  }

  /**
   * After the agent confirms the call is over (end_call / farewell), drain speech then disconnect.
   */
  endCallAfterConfirmation({ reason = 'end_call', onDone, fallbackMs = 2500 } = {}) {
    if (this._inactivityPhase === 'ending') return;
    this._inactivityPhase = 'ending';
    this._awaitingFarewellClose = false;
    this._clearFarewellTimers();
    this._stopInactivityWatch(false);
    this.emit('call-ending', { reason });
    this._pendingEndAfterSpeech = {
      reason,
      onDone:
        onDone ||
        (() => {
          this.disconnect();
        }),
    };
    if (this._endFallbackTimer) clearTimeout(this._endFallbackTimer);
    this._endFallbackTimer = setTimeout(() => {
      this._maybeFinishPendingEnd('fallback');
    }, fallbackMs);
    // If nothing is queued to play, hang up quickly.
    if (!this.playbackContext || this.nextPlayTime <= this.playbackContext.currentTime + 0.15) {
      setTimeout(() => this._maybeFinishPendingEnd('quiet'), 400);
    }
  }

  _clearFarewellTimers() {
    if (this._farewellForceTimer) {
      clearTimeout(this._farewellForceTimer);
      this._farewellForceTimer = null;
    }
    if (this._farewellCloseTimer) {
      clearTimeout(this._farewellCloseTimer);
      this._farewellCloseTimer = null;
    }
  }

  _handleUserFarewellText(content) {
    if (this._inactivityPhase === 'ending') return;
    if (!DeviitorVoiceClient.looksLikeUserFarewell(content)) return;

    this.emit('farewell-detected', { role: 'user', content });
    this._awaitingFarewellClose = true;
    this._clearFarewellTimers();
    // Give the agent time to say goodbye + call end_call. Force-hangup if it skips the tool.
    this._farewellForceTimer = setTimeout(() => {
      if (this._inactivityPhase !== 'ending' && this._awaitingFarewellClose) {
        this.endCallAfterConfirmation({ reason: 'user_farewell_timeout', fallbackMs: 1500 });
      }
    }, 8_000);
  }

  setMuted(muted) {
    this.micMuted = Boolean(muted);
    this.emit('mute-changed', { muted: this.micMuted });
    return this.micMuted;
  }

  toggleMute() {
    return this.setMuted(!this.micMuted);
  }

  /** Milliseconds of agent TTS still scheduled (no pad). */
  _playbackRemainingMs() {
    if (!this.playbackContext) return 0;
    return Math.max(0, Math.ceil((this.nextPlayTime - this.playbackContext.currentTime) * 1000));
  }

  /** Hangup / end-call delay: remaining audio plus a short pad. */
  _playbackDrainMs() {
    return Math.min(this._playbackRemainingMs() + 600, 8000) || 800;
  }

  _maybeFinishPendingEnd(trigger) {
    if (!this._pendingEndAfterSpeech) return;
    const pending = this._pendingEndAfterSpeech;
    this._pendingEndAfterSpeech = null;
    if (this._endFallbackTimer) {
      clearTimeout(this._endFallbackTimer);
      this._endFallbackTimer = null;
    }
    const delay = trigger === 'fallback' ? 0 : this._playbackDrainMs();
    setTimeout(() => {
      try {
        pending.onDone?.();
      } catch {}
      this.emit('call-ended', { reason: pending.reason, trigger });
    }, delay);
  }

  _startInactivityWatch() {
    if (!this.inactivityEnabled) return;
    this._inactivityPhase = 'idle';
    this._armInactivityTimer(this.inactivityWarnMs, 'warn');
  }

  _stopInactivityWatch(resetPhase = true) {
    if (this._inactivityTimer) {
      clearTimeout(this._inactivityTimer);
      this._inactivityTimer = null;
    }
    if (resetPhase) this._inactivityPhase = 'idle';
  }

  _armInactivityTimer(ms, kind) {
    if (!this.inactivityEnabled || this._inactivityPhase === 'ending') return;
    if (this._inactivityTimer) clearTimeout(this._inactivityTimer);
    this._inactivityTimer = setTimeout(() => this._onInactivityTick(kind), ms);
  }

  _noteActivity(_source, { isUser = false } = {}) {
    if (!this.inactivityEnabled || this._inactivityPhase === 'ending') return;

    // After the check-in, only real user speech cancels the hang-up countdown.
    if (this._inactivityPhase === 'warned') {
      if (!isUser) return;
      this._inactivityPhase = 'idle';
      this._armInactivityTimer(this.inactivityWarnMs, 'warn');
      this.emit('inactivity-cleared', {});
      return;
    }

    // Idle: any conversational activity pushes the first warning out.
    this._armInactivityTimer(this.inactivityWarnMs, 'warn');
  }

  _onInactivityTick(kind) {
    if (!this.inactivityEnabled || this._inactivityPhase === 'ending') return;

    if (kind === 'warn' && this._inactivityPhase === 'idle') {
      this._inactivityPhase = 'warned';
      this.emit('inactivity-warning', { warnMs: this.inactivityWarnMs });
      this.injectAgentMessage(this.inactivityWarnMessage, 'queue');
      this._armInactivityTimer(this.inactivityEndMs, 'end');
      return;
    }

    if (kind === 'end' && this._inactivityPhase === 'warned') {
      this._inactivityPhase = 'ending';
      this._stopInactivityWatch(false);
      this.emit('inactivity-ending', { endMs: this.inactivityEndMs });
      this.injectAgentMessage(this.inactivityEndMessage, 'interrupt');
      this._pendingEndAfterSpeech = {
        reason: 'inactivity',
        onDone: () => this.disconnect(),
      };
      if (this._endFallbackTimer) clearTimeout(this._endFallbackTimer);
      this._endFallbackTimer = setTimeout(() => {
        this._maybeFinishPendingEnd('fallback');
      }, 5000);
    }
  }

  beginFunctionCalls(count = 1) {
    this._pendingFunctionCalls += Math.max(1, count);
    this._setMicUplinkSuppressed(true, { hold: true });
  }

  endFunctionCall() {
    this._pendingFunctionCalls = Math.max(0, this._pendingFunctionCalls - 1);
    if (this._pendingFunctionCalls === 0 && !this._agentSpeaking) {
      this._scheduleUnmuteAfterPlayback(350);
    }
  }

  /**
   * Run an async tool while keeping the agent turn alive:
   * progress fillers if slow, hard timeout so the model is never left hanging.
   */
  async runFunctionWithWatchdog(label, work, { timeoutMs = 12_000 } = {}) {
    const started = Date.now();
    const progressTimers = [];
    const clearProgress = () => {
      for (const t of progressTimers) clearTimeout(t);
      progressTimers.length = 0;
    };

    progressTimers.push(
      setTimeout(() => {
        this.injectAgentMessage('Still checking that for you.', 'queue');
        this.emit('function-progress', { label, elapsedMs: Date.now() - started });
      }, 1600),
    );
    progressTimers.push(
      setTimeout(() => {
        this.injectAgentMessage('Thanks for waiting — almost done.', 'queue');
        this.emit('function-progress', { label, elapsedMs: Date.now() - started });
      }, 4500),
    );

    try {
      const result = await Promise.race([
        Promise.resolve().then(work),
        new Promise((_, reject) => {
          setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs);
        }),
      ]);
      clearProgress();
      return result;
    } catch (err) {
      clearProgress();
      throw err;
    }
  }

  sendFunctionCallResponse(id, name, content, thoughtSignature) {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      this.endFunctionCall();
      return;
    }
    const payload = {
      type: 'FunctionCallResponse',
      id,
      name,
      content,
    };
    if (thoughtSignature) payload.thought_signature = thoughtSignature;
    this.ws.send(JSON.stringify(payload));
    this.endFunctionCall();
  }

  /**
   * Shared getUserMedia constraints for capture + permission probe.
   */
  static micAudioConstraints() {
    return {
      echoCancellation: { ideal: true },
      noiseSuppression: { ideal: true },
      autoGainControl: { ideal: true },
      channelCount: { ideal: 1 },
      googEchoCancellation: true,
      googNoiseSuppression: true,
      googAutoGainControl: true,
      googHighpassFilter: true,
    };
  }

  /**
   * Ensure mic permission is granted before opening a Deepgram session.
   * Triggers the browser permission prompt when needed.
   * @returns {Promise<MediaStream>}
   */
  static async ensureMicrophoneAccess() {
    if (!navigator.mediaDevices?.getUserMedia) {
      const err = new Error('This browser does not support microphone access.');
      err.code = 'mic_unsupported';
      throw err;
    }

    try {
      if (navigator.permissions?.query) {
        const status = await navigator.permissions.query({ name: 'microphone' });
        if (status.state === 'denied') {
          const err = new Error(
            'Microphone access is blocked. Enable the microphone for this site in your browser settings, then tap the mic again.',
          );
          err.code = 'mic_denied';
          throw err;
        }
      }
    } catch (err) {
      if (err?.code === 'mic_denied') throw err;
      // permissions.query is unsupported or throws on some browsers — fall through to getUserMedia.
    }

    try {
      return await navigator.mediaDevices.getUserMedia({
        audio: DeviitorVoiceClient.micAudioConstraints(),
      });
    } catch (cause) {
      const name = cause?.name || '';
      let message =
        'Could not access the microphone. Check your device settings and try again.';
      let code = 'mic_error';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        message =
          'Microphone permission is required. Allow access when prompted, or enable it in your browser settings, then try again.';
        code = 'mic_denied';
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        message = 'No microphone was found. Connect a mic and try again.';
        code = 'mic_not_found';
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        message = 'Your microphone is busy or unavailable. Close other apps using it, then try again.';
        code = 'mic_busy';
      } else if (cause?.message) {
        message = cause.message;
      }
      const err = new Error(message);
      err.code = code;
      err.cause = cause;
      throw err;
    }
  }

  async startMicrophone(existingStream = null) {
    // Mic capture uses its own AudioContext so ScriptProcessor work cannot underrun TTS.
    this.audioContext = new AudioContext();
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }
    const nativeRate = this.audioContext.sampleRate;

    this.micStream =
      existingStream ||
      (await navigator.mediaDevices.getUserMedia({
        audio: DeviitorVoiceClient.micAudioConstraints(),
      }));

    try {
      const track = this.micStream.getAudioTracks()[0];
      if (track?.applyConstraints) {
        await track.applyConstraints({
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        });
      }
    } catch {}

    // Ensure context is running after the async getUserMedia gap.
    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    const source = this.audioContext.createMediaStreamSource(this.micStream);
    // Larger buffer = fewer main-thread callbacks = smoother concurrent playback.
    const processor = this.audioContext.createScriptProcessor(8192, 1, 1);
    this.processor = processor;
    processor.onaudioprocess = (event) => {
      if (!this.settingsApplied || this.ws?.readyState !== WebSocket.OPEN) return;
      // Keep capture alive if the browser suspended the mic context.
      if (this.audioContext?.state === 'suspended') {
        this.audioContext.resume().catch(() => {});
      }
      const frameSamples = Math.max(
        1,
        Math.round((event.inputBuffer.length * this.inputSampleRate) / nativeRate),
      );
      // Soft mute / agent-playback gate: send silence so the socket stays alive without echo.
      if (this.micMuted || this._suppressMicUplink) {
        this.ws.send(new Int16Array(frameSamples).buffer);
        return;
      }
      const input = event.inputBuffer.getChannelData(0);
      const downsampled = this._downsample(input, nativeRate, this.inputSampleRate);
      const pcm = this._floatTo16BitPCM(downsampled);
      this.ws.send(pcm);
    };
    source.connect(processor);
    // ScriptProcessor must reach destination or some browsers never fire onaudioprocess.
    const muteGain = this.audioContext.createGain();
    muteGain.gain.value = 0;
    processor.connect(muteGain);
    muteGain.connect(this.audioContext.destination);
  }

  _shouldAllowBargeIn() {
    if (this._agentSpeaking || this._suppressMicUplink || this._pendingFunctionCalls > 0) {
      return false;
    }
    if (!this.playbackContext) return true;
    const remaining = this.nextPlayTime - this.playbackContext.currentTime;
    // Still draining TTS audio — treat as echo, not a real interrupt.
    return remaining <= 0.08;
  }

  /**
   * Soft-mute mic uplink (echo gate).
   * @param {boolean} suppressed
   * @param {{ hold?: boolean }} [opts] hold:true cancels a pending unmute (tools / agent turn start)
   */
  _setMicUplinkSuppressed(suppressed, { hold = false } = {}) {
    this._suppressMicUplink = Boolean(suppressed);
    if (hold && suppressed && this._unmuteUplinkTimer) {
      clearTimeout(this._unmuteUplinkTimer);
      this._unmuteUplinkTimer = null;
    }
  }

  _scheduleUnmuteAfterPlayback(padMs = 250) {
    if (this._unmuteUplinkTimer) clearTimeout(this._unmuteUplinkTimer);
    this._suppressMicUplink = true;
    const wait = () => {
      const remainingMs = this._playbackRemainingMs();
      if (remainingMs > 40) {
        this._unmuteUplinkTimer = setTimeout(wait, Math.min(remainingMs, 200));
        return;
      }
      this._unmuteUplinkTimer = setTimeout(() => {
        if (!this._agentSpeaking && this._pendingFunctionCalls === 0) {
          this._suppressMicUplink = false;
        }
        this._unmuteUplinkTimer = null;
      }, padMs);
    };
    wait();
  }

  _downsample(buffer, fromRate, toRate) {
    if (fromRate === toRate) return buffer;
    const ratio = fromRate / toRate;
    const newLength = Math.round(buffer.length / ratio);
    const result = new Float32Array(newLength);
    for (let i = 0; i < newLength; i++) {
      const idx = Math.floor(i * ratio);
      result[i] = buffer[idx];
    }
    return result;
  }

  _floatTo16BitPCM(float32) {
    const buffer = new ArrayBuffer(float32.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < float32.length; i++) {
      let s = Math.max(-1, Math.min(1, float32[i]));
      view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
  }

  async _ensurePlaybackContext() {
    if (!this.playbackContext || this.playbackContext.state === 'closed') {
      // Match Deepgram output rate exactly for gapless TTS (no live resampling).
      this.playbackContext = new AudioContext({ sampleRate: this.outputSampleRate });
      this.nextPlayTime = 0;
      this._activeSources = new Set();
    }
    if (this.playbackContext.state === 'suspended') {
      await this.playbackContext.resume();
    }
  }

  async _playPcm(arrayBuffer) {
    await this._ensurePlaybackContext();
    // Mute uplink for echo, but do not cancel a pending unmute schedule.
    this._suppressMicUplink = true;
    const pcm = new Int16Array(arrayBuffer);
    if (!pcm.length) return;

    const float32 = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) {
      float32[i] = pcm[i] / 0x8000;
    }

    // Play at the buffer's native Deepgram rate; the browser handles device output.
    const rate = this.outputSampleRate;
    const buffer = this.playbackContext.createBuffer(1, float32.length, rate);
    buffer.copyToChannel(float32, 0);
    const source = this.playbackContext.createBufferSource();
    source.buffer = buffer;
    source.connect(this.playbackContext.destination);

    const now = this.playbackContext.currentTime;
    // Small lookahead only when underrunning — never insert gaps between chunks.
    const LOOKAHEAD = 0.05;
    if (this.nextPlayTime < now) {
      this.nextPlayTime = now + LOOKAHEAD;
    }
    source.start(this.nextPlayTime);
    this.nextPlayTime += buffer.duration;

    this._activeSources.add(source);
    source.onended = () => {
      this._activeSources.delete(source);
    };

    // Late TTS chunks after AgentAudioDone must re-arm unmute or the mic stays dead.
    if (!this._agentSpeaking && this._pendingFunctionCalls === 0) {
      this._scheduleUnmuteAfterPlayback(300);
    }
  }

  _interruptPlayback() {
    for (const source of this._activeSources) {
      try {
        source.stop(0);
      } catch {}
    }
    this._activeSources.clear();
    // Reset schedule; keep the dedicated playback context alive for smooth resume.
    this.nextPlayTime = this.playbackContext ? this.playbackContext.currentTime : 0;
    this._agentSpeaking = false;
    if (this._pendingFunctionCalls === 0) {
      this._setMicUplinkSuppressed(false);
    }
  }

  stopMicrophone() {
    if (this._unmuteUplinkTimer) {
      clearTimeout(this._unmuteUplinkTimer);
      this._unmuteUplinkTimer = null;
    }
    this._suppressMicUplink = false;
    this._agentSpeaking = false;
    try {
      this.processor?.disconnect();
    } catch {}
    this.processor = null;
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.micStream = null;
    try {
      // Mic context is separate from playback — safe to close.
      if (this.audioContext && this.audioContext !== this.playbackContext) {
        this.audioContext.close();
      }
    } catch {}
    this.audioContext = null;
  }

  disconnect() {
    this.intentionalClose = true;
    this._stopKeepAlive();
    this._stopInactivityWatch();
    this._clearFarewellTimers();
    this._awaitingFarewellClose = false;
    this._pendingEndAfterSpeech = null;
    if (this._endFallbackTimer) {
      clearTimeout(this._endFallbackTimer);
      this._endFallbackTimer = null;
    }
    if (this._unmuteUplinkTimer) {
      clearTimeout(this._unmuteUplinkTimer);
      this._unmuteUplinkTimer = null;
    }
    this.micMuted = false;
    this._suppressMicUplink = false;
    this._agentSpeaking = false;
    this._pendingFunctionCalls = 0;
    this.stopMicrophone();
    this._interruptPlayback();
    try {
      this.playbackContext?.close();
    } catch {}
    this.playbackContext = null;
    this.audioContext = null;
    try {
      this.ws?.close();
    } catch {}
    this.ws = null;
    this.settingsApplied = false;
  }
}
